(() => {
  if (window.KayozQuickCartLoaded) return;
  window.KayozQuickCartLoaded = true;

  const ROOT_SELECTOR = "[data-kayoz-quick-cart]";
  const ADD_BUTTON_SELECTOR = "[data-kayoz-rec-add]";
  const SIZE_BUTTON_SELECTOR = "[data-kayoz-rec-size]";
  const SIZE_PICKER_SELECTOR = "[data-kayoz-rec-picker]";
  const SIZE_PICKER_CLOSE_SELECTOR = "[data-kayoz-rec-picker-close]";
  const MUTATION_CONTROL_SELECTOR =
    "[data-remove-item], [data-add-quantity], [data-subtract-quantity], [data-kayoz-rec-add]";
  const NATIVE_CART_REFRESH_EVENT = "apps:product-added-to-cart";

  let pendingRecommendation = null;
  let pendingCompletionMessage = null;
  let busyTimer = null;
  let pickerTrigger = null;
  let viewportSyncReady = false;

  const getRoot = () => document.querySelector(ROOT_SELECTOR);

  const announce = (root, message) => {
    if (!root || !message) return;
    const announcer = root.querySelector("[data-kayoz-cart-announcer]");
    if (!announcer) return;
    announcer.textContent = "";
    window.requestAnimationFrame(() => {
      announcer.textContent = message;
    });
  };

  const setBusy = (root, busy, message) => {
    if (!root) return;
    const checkout = root.querySelector(".quick-cart__submit");
    root.classList.toggle("is-updating", busy);
    root.setAttribute("aria-busy", String(busy));

    if (checkout) {
      const cartBlocked = checkout.dataset.cartBlocked === "true";
      checkout.disabled = busy || cartBlocked;
      checkout.setAttribute("aria-disabled", String(busy || cartBlocked));
    }

    if (!busy) {
      root.querySelectorAll(ADD_BUTTON_SELECTOR + ".loading").forEach((button) => {
        button.classList.remove("loading");
      });
    }

    window.clearTimeout(busyTimer);
    busyTimer = null;

    if (message) announce(root, message);

    if (busy) {
      busyTimer = window.setTimeout(() => {
        setBusy(root, false, root.dataset.errorMessage);
        pendingRecommendation = null;
        pendingCompletionMessage = null;
      }, 12000);
    }
  };

  const recommendationDetail = (card, variantId, size) => ({
    productId: card?.dataset.productId || null,
    productHandle: card?.dataset.productHandle || null,
    variantId: variantId || null,
    size: size || null,
    sourceGroup: card?.dataset.cardSource || null,
    source: "quick-cart",
  });

  const syncItemsPadding = (root) => {
    window.requestAnimationFrame(() => {
      const items = root?.querySelector(".quick-cart__items");
      const footer = root?.querySelector(".quick-cart__footer");
      if (!items || !footer) return;
      const footerHeight = `${footer.clientHeight}px`;
      items.style.paddingBottom = footerHeight;
      items.style.scrollPaddingBottom = footerHeight;
    });
  };

  const closeSizePicker = (root, restoreFocus = true) => {
    const picker = root?.querySelector(SIZE_PICKER_SELECTOR);
    const trigger = pickerTrigger;
    if (!picker) {
      pickerTrigger = null;
      return false;
    }
    if (picker.hidden) {
      pickerTrigger = null;
      return false;
    }

    picker.hidden = true;
    picker.querySelector("[data-kayoz-rec-picker-options]")?.replaceChildren();
    trigger?.setAttribute("aria-expanded", "false");
    pickerTrigger = null;

    if (restoreFocus) trigger?.focus();
    return true;
  };

  const openSizePicker = (button, card, root) => {
    const picker = root?.querySelector(SIZE_PICKER_SELECTOR);
    const template = card?.querySelector("[data-kayoz-rec-size-template]");
    const options = picker?.querySelector("[data-kayoz-rec-picker-options]");
    if (!picker || !template || !options) {
      announce(root, root?.dataset.errorMessage);
      return;
    }

    closeSizePicker(root, false);
    pickerTrigger = button;
    picker.querySelector("[data-kayoz-rec-picker-title]").textContent = card.dataset.productTitle || "";
    options.replaceChildren(template.content.cloneNode(true));
    picker.hidden = false;
    button.setAttribute("aria-expanded", "true");
    syncItemsPadding(root);

    window.requestAnimationFrame(() => {
      picker.querySelector(SIZE_BUTTON_SELECTOR)?.focus();
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      picker.scrollIntoView({ block: "nearest", behavior: reduceMotion ? "auto" : "smooth" });
    });
  };

  const chooseRecommendationSize = (sizeButton, event) => {
    const root = sizeButton.closest(ROOT_SELECTOR);
    const trigger = pickerTrigger;
    const card = trigger?.closest("[data-kayoz-cart-recommendation]");
    if (!root || !trigger || !card) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    trigger.dataset.addItemId = sizeButton.dataset.variantId || "";
    trigger.dataset.selectedSize = sizeButton.dataset.variantTitle || sizeButton.textContent.trim();
    trigger.dataset.selectedStatus = sizeButton.dataset.variantStatus || "";
    const label = trigger.querySelector("[data-kayoz-rec-add-label]");
    if (label) label.textContent = trigger.dataset.readyLabel;
    if (trigger.dataset.readyLabel) {
      trigger.setAttribute(
        "aria-label",
        `${trigger.dataset.readyLabel} — ${card.dataset.productTitle || ""}`.trim(),
      );
    }
    const status = card.querySelector("[data-kayoz-rec-status]");
    if (status) status.textContent = trigger.dataset.selectedStatus;

    card.dispatchEvent(
      new CustomEvent("kayoz:cart-recommendation-selected", {
        bubbles: true,
        detail: recommendationDetail(card, trigger.dataset.addItemId, trigger.dataset.selectedSize),
      }),
    );

    closeSizePicker(root, false);
    trigger.click();
  };

  const handleRecommendationClick = (button, event) => {
    const variantId = button.dataset.addItemId;
    const card = button.closest("[data-kayoz-cart-recommendation]");
    const root = button.closest(ROOT_SELECTOR);

    if (!variantId) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (button.dataset.requiresSize === "true") openSizePicker(button, card, root);
      else announce(root, root?.dataset.errorMessage);
      return;
    }

    pendingRecommendation = recommendationDetail(card, variantId, button.dataset.selectedSize);
    setBusy(root, true, root?.dataset.updatingMessage);
  };

  const finishMutation = (root) => {
    closeSizePicker(root, false);
    syncItemsPadding(root);
    const checkout = root.querySelector(".quick-cart__submit");
    if (checkout) {
      checkout.dataset.cartBlocked = String(Boolean(root.querySelector("[data-kayoz-cart-blocked]")));
    }

    if (!root?.classList.contains("is-updating")) return;

    setBusy(root, false, pendingCompletionMessage || root.dataset.updatedMessage);
    pendingCompletionMessage = null;
    if (pendingRecommendation) {
      root.dispatchEvent(
        new CustomEvent("kayoz:cart-recommendation-added", {
          bubbles: true,
          detail: pendingRecommendation,
        }),
      );
      pendingRecommendation = null;
    }
  };

  const observeCartUpdates = (root) => {
    const container = root.querySelector(".quick-cart__container");
    if (!container || container.dataset.kayozObserverReady === "true") return;

    container.dataset.kayozObserverReady = "true";
    let previousItems = container.querySelector(".quick-cart__items");

    const observer = new MutationObserver((records) => {
      const currentItems = container.querySelector(".quick-cart__items");
      const itemsChanged = records.some(
        (record) => record.target === currentItems || record.target === previousItems,
      );

      if (currentItems !== previousItems || itemsChanged) {
        previousItems = currentItems;
        finishMutation(root);
      }
    });

    observer.observe(container, { childList: true, subtree: true });
  };

  const observeDrawerVisibility = (root) => {
    const wrapper = root.querySelector(".quick-cart__wrapper");
    if (!wrapper || wrapper.dataset.kayozVisibilityReady === "true") return;

    wrapper.dataset.kayozVisibilityReady = "true";
    let wasOpen = wrapper.classList.contains("active");

    const observer = new MutationObserver(() => {
      const isOpen = wrapper.classList.contains("active");
      if (isOpen && !wasOpen) {
        syncItemsPadding(root);
        root.dispatchEvent(
          new CustomEvent("kayoz:cart-view", {
            bubbles: true,
            detail: { context: "dialog" },
          }),
        );
      }
      wasOpen = isOpen;
    });

    observer.observe(wrapper, { attributes: true, attributeFilter: ["class"] });
  };

  const initializeRoot = () => {
    const root = getRoot();
    if (!root) return;
    observeCartUpdates(root);
    observeDrawerVisibility(root);
    syncItemsPadding(root);

    if (!viewportSyncReady) {
      const syncOnResize = () => syncItemsPadding(getRoot());
      window.addEventListener("resize", syncOnResize, { passive: true });
      window.visualViewport?.addEventListener("resize", syncOnResize, { passive: true });
      viewportSyncReady = true;
    }
  };

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    const root = getRoot();
    const wrapper = root?.querySelector(".quick-cart__wrapper.active");
    if (!wrapper) return;
    event.preventDefault();
    if (closeSizePicker(root)) return;
    wrapper.querySelector(".quick-cart__close-icon")?.click();
  });

  document.addEventListener(
    "click",
    (event) => {
      const sizeButton = event.target.closest?.(SIZE_BUTTON_SELECTOR);
      if (sizeButton) {
        chooseRecommendationSize(sizeButton, event);
        return;
      }

      const pickerClose = event.target.closest?.(SIZE_PICKER_CLOSE_SELECTOR);
      if (pickerClose) {
        event.preventDefault();
        closeSizePicker(pickerClose.closest(ROOT_SELECTOR));
        return;
      }

      const control = event.target.closest?.(MUTATION_CONTROL_SELECTOR);
      if (!control) return;

      const root = control.closest(ROOT_SELECTOR);
      if (!root) return;

      if (control.matches(ADD_BUTTON_SELECTOR)) {
        handleRecommendationClick(control, event);
        return;
      }

      setBusy(root, true, root.dataset.updatingMessage);
    },
    true,
  );

  document.addEventListener("shopify:cart:lines-update", (event) => {
    const root = getRoot();
    if (!root || !event.promise) return;

    setBusy(root, true, root.dataset.updatingMessage);
    event.promise
      .then((result) => {
        if (result?.userErrors?.length) {
          setBusy(root, false, result.userErrors[0].message || root.dataset.errorMessage);
          pendingRecommendation = null;
          pendingCompletionMessage = null;
          return;
        }

        if (result?.warnings?.length) {
          pendingCompletionMessage = result.warnings[0].message;
        }
        document.dispatchEvent(new CustomEvent(NATIVE_CART_REFRESH_EVENT));
      })
      .catch(() => {
        setBusy(root, false, root.dataset.errorMessage);
        pendingRecommendation = null;
        pendingCompletionMessage = null;
      });
  });

  document.addEventListener("shopify:cart:error", (event) => {
    const root = getRoot();
    if (!root) return;
    const message = event.error || event.detail?.error || root.dataset.errorMessage;
    setBusy(root, false, message);
    pendingRecommendation = null;
    pendingCompletionMessage = null;
  });

  document.addEventListener("DOMContentLoaded", () => {
    initializeRoot();

    const actions = window.Shopify?.actions;
    if (!actions) return;

    if (actions.openCart?.isDefault?.()) {
      actions.openCart.configure({
        handler() {
          const cartTrigger = document.querySelector("[data-js-cart-icon]");
          if (cartTrigger) {
            cartTrigger.click();
            return;
          }
          window.location.assign((window.Shopify?.routes?.root || "/") + "cart");
        },
      });
    }

    if (actions.updateCart?.isDefault?.()) {
      actions.updateCart.configure({
        eventTarget() {
          return getRoot() || document.body;
        },
      });
    }
  });

  document.addEventListener("shopify:section:load", initializeRoot);
})();
